import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCurrency, formatDate, nightsBetween } from "@/lib/format";
import { ChevronLeft, Mail, Phone, FileText, Tag, Star, Plus, X } from "lucide-react";
import { toast } from "sonner";

const LOYALTY_TIERS = [
    { key: "none", label: "Regular", color: "bg-slate-100 text-slate-700" },
    { key: "silver", label: "Silver", color: "bg-slate-200 text-slate-800" },
    { key: "gold", label: "Gold", color: "bg-amber-100 text-amber-800" },
    { key: "platinum", label: "Platinum", color: "bg-orange-100 text-orange-800" },
];

export default function GuestDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [guest, setGuest] = useState(null);
    const [stays, setStays] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [tab, setTab] = useState("profile");
    const [edit, setEdit] = useState({});

    const load = async () => {
        try {
            const [g, s, i] = await Promise.all([
                api.get(`/guests/${id}`),
                api.get(`/guests/${id}/stays`),
                api.get(`/guests/${id}/invoices`),
            ]);
            setGuest(g.data);
            setEdit(g.data);
            setStays(s.data);
            setInvoices(i.data);
        } catch { toast.error("Failed to load guest"); navigate("/guests"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const save = async () => {
        try { await api.patch(`/guests/${id}`, edit); toast.success("Saved"); await load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const addPreference = () => {
        const p = window.prompt("Add preference (e.g. 'high floor', 'allergies: nuts'):");
        if (!p) return;
        const prefs = [...(edit.preferences || []), p.trim()];
        setEdit({ ...edit, preferences: prefs });
        api.patch(`/guests/${id}`, { preferences: prefs }).then(load);
    };
    const removePreference = (p) => {
        const prefs = (edit.preferences || []).filter((x) => x !== p);
        setEdit({ ...edit, preferences: prefs });
        api.patch(`/guests/${id}`, { preferences: prefs }).then(load);
    };

    const addDocument = () => {
        const name = window.prompt("Document name (e.g. 'Passport scan'):"); if (!name) return;
        const url = window.prompt("Document URL:"); if (!url) return;
        const docs = [...(edit.documents || []), { name, url, added_at: new Date().toISOString() }];
        api.patch(`/guests/${id}`, { documents: docs }).then(load);
    };
    const removeDocument = (idx) => {
        const docs = (edit.documents || []).filter((_, i) => i !== idx);
        api.patch(`/guests/${id}`, { documents: docs }).then(load);
    };

    if (!guest) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const totalSpent = stays.filter((s) => s.status !== "cancelled").reduce((a, b) => a + (b.total_amount || 0), 0);
    const totalNights = stays.filter((s) => s.status !== "cancelled").reduce((a, b) => a + nightsBetween(b.check_in, b.check_out), 0);
    const tier = LOYALTY_TIERS.find((t) => t.key === (guest.loyalty_tier || "none")) || LOYALTY_TIERS[0];

    return (
        <>
            <Topbar
                title={`${guest.first_name} ${guest.last_name}`}
                subtitle={`Guest profile · ${guest.email || guest.phone || "—"}`}
                actions={<button onClick={() => navigate("/guests")} className="akm-btn-ghost"><ChevronLeft size={14} /> Back</button>}
            />
            <div className="flex-1 overflow-auto p-6">
                {/* Header card */}
                <div className="akm-card mb-4 p-5">
                    <div className="flex items-start gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
                            {guest.first_name?.[0]}{guest.last_name?.[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <div className="text-base font-semibold tracking-tight">{guest.first_name} {guest.last_name}</div>
                                <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${tier.color}`}>
                                    <Star size={9} className="mr-0.5 inline" />{tier.label}
                                </span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
                                {guest.email && <span className="inline-flex items-center gap-1"><Mail size={11} />{guest.email}</span>}
                                {guest.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{guest.phone}</span>}
                                {guest.country && <span>· {guest.country}</span>}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-right">
                            <Stat label="Stays" value={stays.length} />
                            <Stat label="Nights" value={totalNights} />
                            <Stat label="Spent" value={formatCurrency(totalSpent)} />
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-4 flex gap-1 border-b border-border">
                    {[
                        ["profile", "Profile"], ["stays", `Stay history (${stays.length})`],
                        ["documents", `Documents (${guest.documents?.length || 0})`],
                        ["preferences", `Preferences (${guest.preferences?.length || 0})`],
                        ["invoices", `Invoices (${invoices.length})`],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            data-testid={`guest-tab-${k}`}
                        >{label}</button>
                    ))}
                </div>

                {/* Profile tab */}
                {tab === "profile" && (
                    <div className="akm-card max-w-2xl p-5 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <FieldLabel label="First name"><input className="akm-input" value={edit.first_name || ""} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} /></FieldLabel>
                            <FieldLabel label="Last name"><input className="akm-input" value={edit.last_name || ""} onChange={(e) => setEdit({ ...edit, last_name: e.target.value })} /></FieldLabel>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <FieldLabel label="Email"><input type="email" className="akm-input" value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></FieldLabel>
                            <FieldLabel label="Phone"><input className="akm-input" value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></FieldLabel>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <FieldLabel label="Country"><input className="akm-input" value={edit.country || ""} onChange={(e) => setEdit({ ...edit, country: e.target.value })} /></FieldLabel>
                            <FieldLabel label="Document type"><input className="akm-input" value={edit.document_type || ""} onChange={(e) => setEdit({ ...edit, document_type: e.target.value })} placeholder="passport, id_card…" /></FieldLabel>
                            <FieldLabel label="Document number"><input className="akm-input" value={edit.document_number || ""} onChange={(e) => setEdit({ ...edit, document_number: e.target.value })} /></FieldLabel>
                        </div>
                        <FieldLabel label="Loyalty tier">
                            <select className="akm-input" value={edit.loyalty_tier || "none"} onChange={(e) => setEdit({ ...edit, loyalty_tier: e.target.value })}>
                                {LOYALTY_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                        </FieldLabel>
                        <FieldLabel label="Notes"><textarea className="akm-input h-24 py-2" value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></FieldLabel>
                        <div className="flex justify-end pt-2">
                            <button onClick={save} className="akm-btn-primary" data-testid="guest-save">Save changes</button>
                        </div>
                    </div>
                )}

                {/* Stays tab */}
                {tab === "stays" && (
                    <div className="akm-card overflow-hidden">
                        <table className="akm-table w-full">
                            <thead className="bg-muted/40"><tr><th>Check-in</th><th>Check-out</th><th>Nights</th><th>Status</th><th>Source</th><th>Amount</th><th>Payment</th></tr></thead>
                            <tbody>
                                {stays.map((s) => (
                                    <tr key={s.id}>
                                        <td>{formatDate(s.check_in)}</td>
                                        <td>{formatDate(s.check_out)}</td>
                                        <td>{nightsBetween(s.check_in, s.check_out)}</td>
                                        <td><StatusBadge status={s.status} /></td>
                                        <td className="capitalize text-muted-foreground">{s.source || "direct"}</td>
                                        <td className="font-mono">{formatCurrency(s.total_amount)}</td>
                                        <td><StatusBadge status={s.payment_status === "paid" ? "active" : s.payment_status === "partial" ? "trial" : "pending"} label={s.payment_status || "pending"} /></td>
                                    </tr>
                                ))}
                                {stays.length === 0 && <tr><td colSpan="7" className="py-12 text-center text-muted-foreground">No stays yet</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Documents tab */}
                {tab === "documents" && (
                    <div>
                        <button onClick={addDocument} className="akm-btn-primary mb-3" data-testid="add-doc"><Plus size={14} /> Add document</button>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                            {(guest.documents || []).map((d, idx) => (
                                <div key={idx} className="akm-card flex items-center gap-3 p-3">
                                    <FileText size={16} className="text-muted-foreground" />
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-medium">{d.name}</div>
                                        <a href={d.url} target="_blank" rel="noreferrer" className="truncate text-[11px] text-primary hover:underline">{d.url}</a>
                                    </div>
                                    <button onClick={() => removeDocument(idx)} className="rounded-md p-1 text-red-600 hover:bg-red-50"><X size={12} /></button>
                                </div>
                            ))}
                            {(guest.documents || []).length === 0 && (
                                <div className="col-span-full rounded-[10px] border border-dashed border-border bg-card/50 py-12 text-center text-sm text-muted-foreground">No documents</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Preferences tab */}
                {tab === "preferences" && (
                    <div className="akm-card max-w-2xl p-5">
                        <div className="mb-3 flex items-center justify-between">
                            <div className="akm-section-title">Guest preferences</div>
                            <button onClick={addPreference} className="akm-btn-ghost" data-testid="add-pref"><Plus size={12} /> Add</button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {(guest.preferences || []).map((p) => (
                                <span key={p} className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[12px]">
                                    <Tag size={10} className="text-muted-foreground" />{p}
                                    <button onClick={() => removePreference(p)} className="text-muted-foreground hover:text-red-600"><X size={10} /></button>
                                </span>
                            ))}
                            {(guest.preferences || []).length === 0 && <div className="text-[12px] text-muted-foreground">No preferences recorded yet.</div>}
                        </div>
                    </div>
                )}

                {/* Invoices tab */}
                {tab === "invoices" && (
                    <div className="akm-card overflow-hidden">
                        <table className="akm-table w-full">
                            <thead className="bg-muted/40"><tr><th>Invoice</th><th>Stay</th><th>Amount</th><th>Paid</th><th>Status</th></tr></thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td className="font-mono text-[11px]">{inv.invoice_number}</td>
                                        <td className="text-muted-foreground">{formatDate(inv.check_in)} → {formatDate(inv.check_out)}</td>
                                        <td className="font-mono">{formatCurrency(inv.total_amount)}</td>
                                        <td className="font-mono">{formatCurrency(inv.paid_amount)}</td>
                                        <td><StatusBadge status={inv.payment_status === "paid" ? "active" : inv.payment_status === "partial" ? "trial" : "pending"} label={inv.payment_status} /></td>
                                    </tr>
                                ))}
                                {invoices.length === 0 && <tr><td colSpan="5" className="py-12 text-center text-muted-foreground">No invoices</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-base font-semibold tracking-tight">{value}</div>
        </div>
    );
}

function FieldLabel({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}
